import { useState, useEffect } from 'react'
import {
  Container,
  Title,
  Stack,
  Grid,
  Card,
  Text,
  Group,
  Button,
  Loader,
  Center,
  Badge,
  Table,
  ScrollArea,
  TextInput,
  MultiSelect,
  Select,
  Pagination,
  Chip,
  Tooltip,
  Paper,
} from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import { IconCalendar, IconRefresh, IconSearch, IconDownload, IconLoader, IconLock } from '@tabler/icons-react'
import { useRoutingRows, useRoutingMetrics, useOffensesBySearchId } from '@/hooks/useAnalytics'
import { exportAllRoutingDashboardRows } from '@/services/analytics.api'
import { usePermissions } from '@/hooks/usePermissions'
import { useVersionContext } from '@/context/VersionContext'
import { OffenseDetailDialog } from '@/components/OffenseDetailDialog'

// External-safe columns plus UBS decision/notes. Other internal analytics fields
// (QA decisions, error status, normalized classifications, validation, run history)
// are excluded.
const availableColumns = [
  { value: 'search_id', label: 'Search ID' },
  { value: 'offense_id', label: 'Offense ID' },
  { value: 'case_number', label: 'Case Number' },
  { value: 'search_type', label: 'Product' },
  { value: 'source_norm', label: 'Source' },
  { value: 'offense_type', label: 'Offense Type' },
  { value: 'charge', label: 'Charge' },
  { value: 'disposition', label: 'Disposition' },
  { value: 'file_date', label: 'File Date' },
  { value: 'disposition_date', label: 'Disp Date' },
  { value: 'match_status', label: 'Match Status' },
  { value: 'queue', label: 'AI Decision' },
  { value: 'ground_truth', label: 'UBS Decision' },
  { value: 'all_notes', label: 'UBS Notes' },
  { value: 'rules_applied', label: 'Rules Applied' },
  { value: 'rationale', label: 'AI Rationale' },
]

// Client-facing labels for the internal queue values
const QUEUE_DISPLAY: Record<string, { label: string; color: string }> = {
  AUDITOR: { label: 'Report.', color: 'red' },
  AUTOMATION: { label: 'Non-Rep.', color: 'green' },
  INSUFFICIENT_DATA: { label: 'Review', color: 'yellow' },
  'INSUFFICIENT DATA': { label: 'Review', color: 'yellow' },
}

const SOURCE_OPTIONS = [
  'Courts',
  'Department of Corrections',
  'Offender Registries',
  'Other',
  'Registries and International',
  'Warrant and Wanted',
]

const defaultColumns = [
  'search_id',
  'offense_id',
  'case_number',
  'source_norm',
  'offense_type',
  'charge',
  'disposition',
  'file_date',
  'disposition_date',
  'match_status',
  'queue',
  'ground_truth',
  'all_notes',
]

export function OffenseRecordsPage() {
  const { canExportFilteredData, isAdmin } = usePermissions()
  // Same as RoutingDashboard: use the global version selection as-is (null =
  // no version filter). Do NOT fall back to currentDeployedVersion — old data
  // may not exist under the latest version and would come back empty.
  const { selectedVersion } = useVersionContext()
  const modelVersion = selectedVersion

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    new Date(2026, 4, 1),
    new Date(2026, 6, 31),
  ])
  const [searchId, setSearchId] = useState('')
  const [queueFilter, setQueueFilter] = useState<string[]>([])
  const [matchStatusFilter, setMatchStatusFilter] = useState<string[]>([])
  const [sourceFilter, setSourceFilter] = useState<string | null>(null)
  const [productFilter, setProductFilter] = useState<string | null>(null)

  const [selectedColumns, setSelectedColumns] = useState<string[]>(defaultColumns)
  const [page, setPage] = useState(1)
  const [isExporting, setIsExporting] = useState(false)
  const [exportProgress, setExportProgress] = useState<string | null>(null)
  const pageSize = 50

  // Offense detail dialog state
  const [selectedOffense, setSelectedOffense] = useState<any>(null)
  const [dialogOpened, setDialogOpened] = useState(false)

  const filterParams = {
    startDate: dateRange[0]?.toISOString().split('T')[0] || '',
    endDate: dateRange[1]?.toISOString().split('T')[0] || '',
    searchId,
    queue: queueFilter,
    matchStatus: matchStatusFilter,
    ubsStatus: ['REMAINING'],
    modelVersion: modelVersion || '',
    sourceNorm: sourceFilter && sourceFilter !== '__ALL__' ? [sourceFilter] : [],
    searchType: productFilter && productFilter !== '__ALL__' ? [productFilter] : [],
  }

  const { data: metricsData } = useRoutingMetrics(filterParams)
  const availableSearchTypes = metricsData?.metrics?.availableSearchTypes || []

  const { data: rowsData, isLoading, isFetching, refetch } = useRoutingRows({
    ...filterParams,
    page,
    pageSize,
  })

  useEffect(() => {
    setPage(1)
  }, [dateRange, searchId, queueFilter, matchStatusFilter, sourceFilter, productFilter, modelVersion])

  const rows = rowsData?.rows || []
  const totalCount = rowsData?.totalCount || 0
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))

  // All offenses for the selected search_id, shown in the detail dialog
  const { data: offensesBySearchData } = useOffensesBySearchId(
    selectedOffense?.search_id || null,
    modelVersion || undefined,
  )

  const downloadCSV = async () => {
    if (totalCount === 0) return
    setIsExporting(true)
    setExportProgress(`0 / ${totalCount.toLocaleString()}`)
    try {
      const exportResponse = await exportAllRoutingDashboardRows(
        filterParams,
        (fetched, total) => {
          setExportProgress(`${fetched.toLocaleString()} / ${total.toLocaleString()}`)
        },
      )
      const allRows: any[] = exportResponse.rows || []
      if (allRows.length === 0) {
        alert('Export completed but returned 0 rows. Try narrowing the date range or filters.')
        return
      }

      const columnsToExport = selectedColumns.filter(col =>
        availableColumns.some(c => c.value === col)
      )
      if (columnsToExport.length === 0) {
        alert('Select at least one column to export.')
        return
      }
      const header = columnsToExport
        .map(col => availableColumns.find(c => c.value === col)?.label || col)
        .join(',')

      const renderValueForCSV = (value: any) => {
        if (value === null || value === undefined) return ''
        if (typeof value === 'boolean') return value ? 'Yes' : 'No'
        if (Array.isArray(value)) return value.join('; ')
        if (typeof value === 'object') {
          // BigQuery DATE/TIMESTAMP fields come as { value: "YYYY-MM-DD" }
          if (value?.value != null) return String(value.value)
          return JSON.stringify(value)
        }
        return String(value)
      }

      const csvRows = allRows.map((record: any) =>
        columnsToExport.map(col => {
          let rawValue = record[col]
          if (col === 'queue') {
            rawValue = QUEUE_DISPLAY[String(rawValue ?? '').toUpperCase()]?.label ?? rawValue
          }
          const stringValue = renderValueForCSV(rawValue)
          if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
            return `"${stringValue.replace(/"/g, '""')}"`
          }
          return stringValue
        }).join(',')
      )

      const csv = [header, ...csvRows].join('\n')
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `offense_records_${new Date().toISOString().slice(0, 10)}.csv`
      link.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Export failed:', error)
      const message = error instanceof Error ? error.message : String(error)
      alert(`Export failed: ${message}`)
    } finally {
      setIsExporting(false)
      setExportProgress(null)
    }
  }

  const renderCell = (record: any, colValue: string) => {
    const value = record[colValue]

    const renderText = (val: any): string => {
      if (val === null || val === undefined) return 'None'
      if (Array.isArray(val)) return val.join(', ')
      if (typeof val === 'object') {
        if (val?.value != null) return String(val.value)
        return JSON.stringify(val)
      }
      return String(val)
    }

    switch (colValue) {
      case 'search_id':
      case 'offense_id':
        return <Text size="xs" ff="monospace">{value || 'None'}</Text>

      case 'queue': {
        const display = QUEUE_DISPLAY[String(value ?? '').toUpperCase()]
        return (
          <Badge size="xs" color={display?.color || 'gray'}>
            {display?.label || value || 'None'}
          </Badge>
        )
      }

      case 'match_status': {
        const matchLabel = (value === 'MANUAL_REVIEW' || value === 'NO_COMPARISON') ? 'Review'
          : value === 'MATCH' ? 'Match'
          : value === 'MISMATCH' ? 'Mismatch'
          : value || 'None'
        return (
          <Badge
            size="xs"
            color={value === 'MATCH' ? 'green' : value === 'MISMATCH' ? 'red' : 'gray'}
          >
            {matchLabel}
          </Badge>
        )
      }

      case 'ground_truth': {
        if (!value) return <Text size="xs" c="dimmed">—</Text>
        const gtLabel = value === 'REPORTABLE' ? 'Report.'
          : value === 'NOT_REPORTABLE' ? 'Non-Rep.'
          : value
        return (
          <Badge
            size="xs"
            color={value === 'REPORTABLE' ? 'red' : value === 'NOT_REPORTABLE' ? 'green' : 'gray'}
          >
            {gtLabel}
          </Badge>
        )
      }

      case 'all_notes': {
        if (!value) return <Text size="xs" c="dimmed">—</Text>
        return (
          <Tooltip label={renderText(value)} withArrow multiline maw={400}>
            <Text size="xs" lineClamp={2}>{renderText(value)}</Text>
          </Tooltip>
        )
      }

      case 'charge':
      case 'charge_description':
      case 'disposition':
      case 'rationale':
      case 'rules_applied':
        return <Text size="xs" lineClamp={2}>{renderText(value)}</Text>

      case 'file_date':
      case 'disposition_date': {
        if (!value) return <Text size="xs">None</Text>
        const raw = typeof value === 'object' && value?.value ? value.value : value
        const date = new Date(String(raw))
        return <Text size="xs">{isNaN(date.getTime()) ? String(raw) : date.toLocaleDateString()}</Text>
      }

      default:
        return <Text size="xs">{renderText(value)}</Text>
    }
  }

  return (
    <Container fluid p="md">
      <Stack gap="xl">
        <Group justify="space-between">
          <div>
            <Title order={2}>Offense Records</Title>
            <Text size="sm" c="dimmed">Full list of processed offenses for your organization</Text>
          </div>
          <Button leftSection={<IconRefresh size={16} />} onClick={() => refetch()} variant="light" size="sm">
            Refresh
          </Button>
        </Group>

        {/* Filters */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="md">
            <Grid>
              <Grid.Col span={{ base: 12, md: 3 }}>
                <DatePickerInput
                  type="range"
                  allowSingleDateInRange
                  label="Search Date Range"
                  placeholder="Select date range"
                  value={dateRange}
                  onChange={setDateRange}
                  leftSection={<IconCalendar size={16} />}
                  minDate={new Date(2026, 4, 1)}
                  maxDate={new Date(2026, 6, 31)}
                  clearable
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 3 }}>
                <TextInput
                  label="Search ID"
                  placeholder="Enter search_id..."
                  leftSection={<IconSearch size={16} />}
                  value={searchId}
                  onChange={(e) => setSearchId(e.target.value)}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 3 }}>
                <MultiSelect
                  label="AI Decision"
                  placeholder="All decisions"
                  data={[
                    { value: 'AUDITOR', label: 'Reportable' },
                    { value: 'AUTOMATION', label: 'Non Reportable' },
                    { value: 'INSUFFICIENT_DATA', label: 'Manual Review' },
                  ]}
                  value={queueFilter}
                  onChange={setQueueFilter}
                  clearable
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 3 }}>
                <MultiSelect
                  label="Match Status"
                  placeholder="All"
                  data={[
                    { value: 'MATCH', label: 'Match' },
                    { value: 'MISMATCH', label: 'Mismatch' },
                    { value: 'NO_COMPARISON', label: 'Manual Review' },
                  ]}
                  value={matchStatusFilter}
                  onChange={setMatchStatusFilter}
                  clearable
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 3 }}>
                <Select
                  label="Product"
                  placeholder="All products"
                  data={[
                    { value: '__ALL__', label: 'All' },
                    ...availableSearchTypes.map(t => ({ value: t, label: t })),
                  ]}
                  value={productFilter}
                  onChange={setProductFilter}
                  clearable
                  searchable
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 3 }}>
                <Select
                  label="Source"
                  placeholder="All sources"
                  data={[
                    { value: '__ALL__', label: 'All' },
                    ...SOURCE_OPTIONS.map(s => ({ value: s, label: s })),
                  ]}
                  value={sourceFilter}
                  onChange={setSourceFilter}
                  clearable
                  searchable
                />
              </Grid.Col>
            </Grid>

            {/* Column Selection */}
            <Stack gap="xs">
              <Text size="sm" fw={500}>Select Columns to Display</Text>
              <Text size="xs" c="dimmed">{selectedColumns.length} of {availableColumns.length} columns selected</Text>
              <Group gap="xs">
                <Chip.Group multiple value={selectedColumns} onChange={setSelectedColumns}>
                  {availableColumns.map(col => (
                    <Chip key={col.value} value={col.value} size="xs">
                      {col.label}
                    </Chip>
                  ))}
                </Chip.Group>
              </Group>
              <Group gap="xs">
                <Button size="xs" variant="subtle" onClick={() => setSelectedColumns(defaultColumns)}>
                  Reset to Default
                </Button>
                <Button size="xs" variant="subtle" onClick={() => setSelectedColumns(availableColumns.map(c => c.value))}>
                  Select All
                </Button>
              </Group>
            </Stack>

            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Showing {rows.length.toLocaleString()} of {totalCount.toLocaleString()} offenses (Page {page} of {totalPages})
              </Text>
              <Group gap="xs">
                {canExportFilteredData ? (
                  <Button
                    leftSection={isExporting ? <IconLoader size={16} className="animate-spin" /> : <IconDownload size={16} />}
                    onClick={downloadCSV}
                    disabled={totalCount === 0 || isExporting}
                    loading={isExporting}
                    variant="filled"
                    size="sm"
                  >
                    {isExporting
                      ? `Exporting${exportProgress ? ` (${exportProgress})` : ''}...`
                      : `Download All (${totalCount.toLocaleString()} records)`}
                  </Button>
                ) : (
                  <Tooltip label="You don't have permission to export data. Contact your administrator.">
                    <Button leftSection={<IconLock size={16} />} disabled variant="outline" size="sm">
                      Download CSV (No Permission)
                    </Button>
                  </Tooltip>
                )}
                {(searchId || queueFilter.length > 0 || matchStatusFilter.length > 0 || sourceFilter || productFilter) && (
                  <Button
                    size="xs"
                    variant="light"
                    onClick={() => {
                      setSearchId('')
                      setQueueFilter([])
                      setMatchStatusFilter([])
                      setSourceFilter(null)
                      setProductFilter(null)
                      setPage(1)
                    }}
                  >
                    Clear Filters
                  </Button>
                )}
              </Group>
            </Group>
          </Stack>
        </Card>

        {/* Table */}
        {isFetching && !isLoading && (
          <Paper p="xs" withBorder radius="md" style={{ backgroundColor: 'var(--mantine-color-blue-0)' }}>
            <Group gap="xs" justify="center">
              <Loader size="xs" />
              <Text size="sm" c="blue">Loading results...</Text>
            </Group>
          </Paper>
        )}

        {isLoading && !rowsData ? (
          <Center style={{ minHeight: '40vh' }}>
            <Loader size="xl" />
          </Center>
        ) : totalCount === 0 ? (
          <Center style={{ minHeight: '40vh' }}>
            <Stack align="center">
              <Text size="xl" c="dimmed">No offenses found for the selected filters</Text>
              <Text size="sm" c="dimmed">Try adjusting your date range or filters</Text>
            </Stack>
          </Center>
        ) : (
          <Card shadow="sm" padding="lg" radius="md" withBorder style={{ opacity: isFetching ? 0.7 : 1 }}>
            <Stack gap="md">
              <ScrollArea>
                <Table striped highlightOnHover withTableBorder withColumnBorders>
                  <Table.Thead>
                    <Table.Tr>
                      {selectedColumns.map(colValue => {
                        const col = availableColumns.find(c => c.value === colValue)
                        return <Table.Th key={colValue} style={{ whiteSpace: 'nowrap' }}>{col?.label || colValue}</Table.Th>
                      })}
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {rows.map((record: any, index: number) => (
                      <Table.Tr
                        key={`${record.offense_id}-${index}`}
                        onClick={() => {
                          setSelectedOffense(record)
                          setDialogOpened(true)
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        {selectedColumns.map(colValue => (
                          <Table.Td
                            key={colValue}
                            style={{
                              maxWidth: '300px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {renderCell(record, colValue)}
                          </Table.Td>
                        ))}
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </ScrollArea>

              {totalPages > 1 && (
                <Group justify="flex-end">
                  <Pagination total={totalPages} value={page} onChange={setPage} size="sm" />
                </Group>
              )}
            </Stack>
          </Card>
        )}
      </Stack>

      {/* Offense Detail Dialog: restricted field set for everyone except admins,
          so expert/external users don't see internal analytics fields */}
      <OffenseDetailDialog
        opened={dialogOpened}
        onClose={() => {
          setDialogOpened(false)
          setSelectedOffense(null)
        }}
        offense={selectedOffense as any}
        allOffensesInSearch={offensesBySearchData?.rows || []}
        isExpert={!isAdmin}
      />
    </Container>
  )
}
